import type { IGroupTransferBeneficiary, } from "@/modules/transaction/common/types";
import { CloudflareAsset } from "@/shared/common/enums";
import { CloudFlareImage } from "@/shared/components";
import type { ICellProps } from "@ab.uitools/ui-library/components/AdvancedTable/types";
import { Button } from "@ab.uitools/ui-library/components/Button";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";

interface Props {
  t: (s: string) => string;
  onEdit: (row: IGroupTransferBeneficiary, index: number) => void;
  onDelete: (index: number) => void;
  isDesktop: boolean;
}

export const getColumns = ({
  t,
  onEdit,
  onDelete,
  isDesktop,
}: Props) => [
  {
    id: "receiverName",
    header: t("transfer.group.beneficiary.name"),
    accessorKey: "receiverName",
    enableColumnDragging: false,
    enableSorting: false,
    enableResizing: false,
  },
  {
    id: "receiverAccount",
    header: t("transfer.group.beneficiary.account"),
    accessorKey: "receiverAccount",
    enableColumnDragging: false,
    enableSorting: false,
    enableResizing: false,
  },
  {
    id: "beneficiaryLegalStatus",
    header: t("transfer.beneficiaryLegalStatus"),
    accessorKey: "beneficiaryLegalStatus",
    enableColumnDragging: false,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }: ICellProps<IGroupTransferBeneficiary>) => {
      const beneficiaryLegalStatus = row.original?.beneficiaryLegalStatus;
      return (
        <Text>{t(`general.${beneficiaryLegalStatus.toLowerCase()}`)}</Text>
      );
    },
  },
  {
    id: "amount",
    header: t("transactionHistoryTable.amount"),
    accessorKey: "amount",
    enableColumnDragging: false,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }: ICellProps<IGroupTransferBeneficiary>) => {
      const amount = row.original?.amount;
      return (
        <Text>{amount}</Text>
      );
    },
  },
  {
    id: "actions",
    header: t("transfer.group.actions"),
    accessorKey: "actions",
    enableColumnDragging: false,
    enableSorting: false,
    enableResizing: false,
    maxSize: 10,
    cell: ({ row }: ICellProps<IGroupTransferBeneficiary>) => {
      return (
        <div className="flexbox align-items--center gap-8">
          <Tooltip
            position={Positions.MIDDLE_LEFT}
            id={`${row.id}_edit-icon-button`}
            text={t("general.edit")}
          />
          <Button
            onClick={() => onEdit(row.original, row.index)}
            type="primary"
            size={isDesktop ? "large" : "medium"}
            id={`${row.id}_edit-icon-button`}
            data-testid="transfer-group-beneficiary-edit"
            data-ga-id="transfer-group-beneficiary-edit"
            data-ga-action="Group transfer beneficiary edit"
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage
                  {...props}
                  className="filter-to-white"
                  path={CloudflareAsset.Edit}
                />
              ),
            }}
          />
          <Tooltip
            position={Positions.MIDDLE_RIGHT}
            id={`${row.id}_delete-icon-button`}
            text={t("general.delete")}
          />
          <Button
            onClick={() => onDelete(row.index)}
            type="secondary"
            size={isDesktop ? "large" : "medium"}
            id={`${row.id}_delete-icon-button`}
            data-testid="transfer-group-beneficiary-delete"
            data-ga-id="transfer-group-beneficiary-delete"
            data-ga-action="Group transfer beneficiary delete"
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
              ),
            }}
          />
        </div>
      );
    },
  },
];
